// tournament/tournamentSave.ts (TF1) - named save + resume of an in-progress tournament. The continuous
// TournamentSessionState (tournamentSession.ts) is plain JSON - the FieldState is numbers + a number[] -
// so a run round-trips losslessly through localStorage. Named for the run (Pro mode later: named for the
// railed pro, per PR0). Same $0 tier the bankroll uses (bankroll.ts); a tournament is a handful of numbers,
// not the /api/sessions D1 path.
//
// Multiple named runs coexist under one store; `loadRun`/`deleteRun` key by name. A run resumes byte-for-
// byte because the FieldState carries the seed + handNo the elimination RNG is derived from - so continuing
// a resumed run produces the identical field churn it would have without the save/reload.
import type { TournamentSessionState } from '../engine/tournamentSession';

/** Bump when the stored shape changes; an old/foreign blob then self-evicts (discard-don't-migrate). */
export const TOURNEY_SAVE_VERSION = 1;

/** The localStorage key for the whole named-runs store. */
export const TOURNEY_SAVE_KEY = 'postflop.tournament.saves';

/** A saved run: the full session state + when it was last touched (for the resume list ordering). */
export interface SavedRun {
  state: TournamentSessionState;
  updatedAt: number;
}

/** A light summary row for the resume picker - enough to identify a run without loading the whole state. */
export interface SavedRunSummary {
  runName: string;
  presetId: TournamentSessionState['presetId'];
  level: number;
  playersLeft: number;
  fieldSize: number;
  heroChips: number;
  result: TournamentSessionState['result'];
  finished: boolean;
  updatedAt: number;
}

interface SaveStore {
  v: number;
  runs: Record<string, SavedRun>;
}

/** SSR/no-storage guard (mirrors bankroll.ts): tolerate a missing localStorage as an empty store. */
const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
};

/** Structural check that a parsed blob is a resumable session (enough to trust it before rendering). */
const isSessionState = (v: unknown): v is TournamentSessionState => {
  if (typeof v !== 'object' || v === null) return false;
  const s = v as TournamentSessionState;
  return (
    typeof s.runName === 'string' &&
    typeof s.seed === 'string' &&
    (s.presetId === 'local_100' || s.presetId === 'local_200') &&
    typeof s.field === 'object' &&
    s.field !== null &&
    Array.isArray(s.field.stacks) &&
    s.field.stacks.every((c) => typeof c === 'number') &&
    typeof s.field.handNo === 'number' &&
    typeof s.field.level === 'number'
  );
};

/** Read the whole store, discarding a version-mismatched or corrupt blob (discard-don't-migrate). */
export const loadSaveStore = (): SaveStore => {
  const ls = storage();
  if (!ls) return { v: TOURNEY_SAVE_VERSION, runs: {} };
  try {
    const raw = ls.getItem(TOURNEY_SAVE_KEY);
    if (!raw) return { v: TOURNEY_SAVE_VERSION, runs: {} };
    const parsed = JSON.parse(raw) as SaveStore;
    if (!parsed || parsed.v !== TOURNEY_SAVE_VERSION || typeof parsed.runs !== 'object') {
      return { v: TOURNEY_SAVE_VERSION, runs: {} };
    }
    const runs: Record<string, SavedRun> = {};
    for (const [name, run] of Object.entries(parsed.runs)) {
      if (run && isSessionState((run as SavedRun).state)) runs[name] = run as SavedRun;
    }
    return { v: TOURNEY_SAVE_VERSION, runs };
  } catch {
    return { v: TOURNEY_SAVE_VERSION, runs: {} };
  }
};

const writeStore = (store: SaveStore): void => {
  const ls = storage();
  if (!ls) return;
  try {
    ls.setItem(TOURNEY_SAVE_KEY, JSON.stringify(store));
  } catch {
    /* quota/denied - the run simply does not persist this reload; never throw into the UI. */
  }
};

/** Save (upsert) a run by its `runName`, stamping `updatedAt`. */
export const saveRun = (state: TournamentSessionState, now: number = Date.now()): void => {
  const store = loadSaveStore();
  store.runs[state.runName] = { state, updatedAt: now };
  writeStore(store);
};

/** All saved runs, newest first, as summaries for the resume picker. */
export const listRuns = (): SavedRunSummary[] =>
  Object.values(loadSaveStore().runs)
    .map(({ state, updatedAt }) => ({
      runName: state.runName,
      presetId: state.presetId,
      level: state.field.level,
      playersLeft: state.field.stacks.length,
      fieldSize: state.field.fieldSize,
      heroChips: state.field.stacks[0] ?? 0,
      result: state.result,
      finished: state.finished,
      updatedAt,
    }))
    .sort((a, b) => b.updatedAt - a.updatedAt);

/** Load one run's full session state by name, or null if it is absent/corrupt. */
export const loadRun = (runName: string): TournamentSessionState | null => {
  const run = loadSaveStore().runs[runName];
  return run && isSessionState(run.state) ? run.state : null;
};

/** Delete one saved run by name. */
export const deleteRun = (runName: string): void => {
  const store = loadSaveStore();
  if (store.runs[runName]) {
    delete store.runs[runName];
    writeStore(store);
  }
};
